import React, { useEffect, useRef } from 'react'
import ReactPlayer from 'react-player'
import { CreationToolSourceFile } from 'site/creation-tools/types'
import styled from 'styled-components'
import { useCreationSourcePreviewUrl } from '../../../hooks/use-creation-source-preview-url'
import { SourceEditorEdits } from '../../source-editor/types'

const Video = styled.div`
    background: #000;
    height: 0;
    position: relative;
    width: 100%;

    canvas {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

const Player = styled(ReactPlayer)`
    left: 0;
    position: absolute;
    top: 0;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
    edits?: SourceEditorEdits
    isMuted: boolean
    isPlaying: boolean
    onTimeUpdate?: (currentTime: number) => void
}

const CreationSourcePlayerVideo = ({
    className,
    source: { height, width, source },
    edits,
    isMuted = true,
    isPlaying = true,
    onTimeUpdate,
}: Props) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const playerRef = useRef<any>(null)
    const previewUrl = useCreationSourcePreviewUrl(source)

    useEffect(() => {
        if (!canvasRef.current || !edits?.frames?.length) return

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const watermark = edits.frames[0].canvas

        canvas.width = width
        canvas.height = height
        context?.drawImage(watermark, edits.crop?.x || 0, edits.crop?.y || 0)
    }, [canvasRef, edits, width, height])

    const onProgress = () => {
        if (!playerRef.current) return

        const currentTime = playerRef.current.getCurrentTime()

        onTimeUpdate && onTimeUpdate(currentTime)

        if (edits?.trim && (currentTime < edits.trim.start || currentTime > edits.trim.end)) {
            playerRef.current.seekTo(edits.trim.start, 'seconds')
        }
    }

    return (
        <Video className={className} style={{ paddingBottom: `${(height / width) * 100}%` }}>
            <Player
                {...{
                    ref: playerRef,
                    url: previewUrl,
                    playing: isPlaying,
                    muted: isMuted,
                    loop: true,
                    playsinline: true,
                    height: '100%',
                    width: '100%',
                    progressInterval: 50,
                    onProgress,
                }}
            />
            {edits?.frames?.length && <canvas ref={canvasRef} />}
        </Video>
    )
}

export default CreationSourcePlayerVideo
